/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import dayjs from "dayjs";
import { FC, type JSX } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { selectCurrentFarmerAccount } from "../../../azure-b2c/storage";
import { useUpdateActivity } from "../../api/hooks/useActivities";
import {
  Activity,
  ActivityStatus,
  ActivityToType,
  Campaign,
  CampaignActivity,
  ContactPerson,
  Lookup,
} from "../../api/types/types";
import { mapBaseActivityInputsToPayload } from "../../utils/mapBaseActivityInputsToPayload";
import CreateOrUpdateActivityModal, {
  CreateOrEditFormValues,
} from "../CreateOrUpdateActivityModal/CreateOrUpdateActivityModal";

export interface EditActivityModalProps {
  open: boolean;
  onClose: () => void;
  contactPersons?: ContactPerson[];
  campaigns?: Campaign[];
  lookups?: Lookup[];
  activity: Activity;
}

const EditActivityModal: FC<EditActivityModalProps> = ({
  open,
  onClose,
  contactPersons = [],
  campaigns = [],
  lookups = [],
  activity,
}): JSX.Element => {
  const currentFarmerAccountNumber = useSelector(selectCurrentFarmerAccount)?.accountNumber ?? "";
  const {
    mutateAsync: updateActivity,
    isPending: isUpdating,
    isError: isUpdateError,
  } = useUpdateActivity(currentFarmerAccountNumber ?? "");

  const {
    control,
    handleSubmit,
    reset,
    trigger: validateActivityForm,
    formState,
    getValues,
  } = useForm<CreateOrEditFormValues>({
    defaultValues: {
      activityStatus: ActivityStatus.Open,
      activityType: activity.activityType,
      subject: activity.subject,
      description: activity.description ?? "",
      scheduleDate: activity.scheduleDate ? dayjs(activity.scheduleDate) : undefined,
      contactId: activity.toType === ActivityToType.Contact ? activity.contactId : undefined,
    },
    mode: "onSubmit",
  });

  const handleUpdateActivity = async (
    values: CreateOrEditFormValues,
    campaignResponses?: CampaignActivity[]
  ): Promise<void> => {
    if (currentFarmerAccountNumber && activity) {
      await updateActivity({
        ...activity,
        ...mapBaseActivityInputsToPayload(currentFarmerAccountNumber, values),
        duration: activity.duration,
        ...(campaignResponses?.length ? { campaignResponses } : {}),
      });
      reset();
      toast.success(
        `Activity ${values.activityStatus === ActivityStatus.Completed ? "completed" : "updated successfully"}`
      );
      onClose();
    }
  };

  const handleClose = (): void => {
    if (!isUpdating) {
      reset();
      onClose();
    }
  };

  const activityBarProps = {
    loadingIndicatorText: "Updating activity",
    closeButtonText: "Cancel",
    submitButtonText: "Update Activity",
    onClose: handleClose,
    onSubmit: handleSubmit((values) => handleUpdateActivity(values)),
    showLoadingIndicator: isUpdating,
    disableSubmit: !formState.isDirty,
  };

  return (
    <CreateOrUpdateActivityModal
      open={open}
      handleClose={onClose}
      control={control}
      validateActivityForm={validateActivityForm}
      title={activity.subject}
      subtitle={`Created by ${activity.createdBy}`}
      contactPersons={contactPersons}
      error={isUpdateError ? "Failed to update activity. Please try again." : undefined}
      owner={activity.owner}
      activityBarProps={activityBarProps}
      disableActivityTypeSelect={true}
      campaigns={campaigns}
      lookups={lookups}
      onStep2Complete={(campaignResponses) => handleUpdateActivity(getValues(), campaignResponses)}
    />
  );
};

export default EditActivityModal;
